import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Option } from 'slim-select/dist/data.d';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { RootState } from '../../../../core/state/state';
import Select from '../../../../components/form/select/Select';
import { InternalUser, InternalUserFilter } from '../../../../core/state/users/types';
import Radio from '../../../../components/form/radio/Radio';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { getInternalUsers, updateInternalUserRole } from '../../../../core/state/users/asyncActions';
import { UserRoleType } from '../../../../core/state/user/types';
import { Color } from '../../../../core/enums/Color';

import './AssignUserRoleModal.scss';

interface Props {
  isOpen: boolean;
  sortFilter: InternalUserFilter;
  closeModal: () => void;
  getUsers: () => void;
}

type FormData = {
  user: string;
  role: UserRoleType;
};

const AssignUserRoleModal: React.FC<Props> = ({ isOpen, closeModal, sortFilter, getUsers }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const users = useSelector((state: RootState) => state.users.internalUsers);

  const usersToOption = (user: InternalUser): Option => ({
    value: user.id.toString(),
    text: `${user.firstName} ${user.lastName} ${user.areaName ? `(${user.areaName})` : ''}`
  });

  const userOptions = useMemo(() => users?.map(usersToOption), [users]);

  const validationSchema = yup
    .object()
    .shape({
      user: yup.string().required(),
      role: yup.string().required()
    })
    .required();

  const {
    handleSubmit,
    control,
    formState: { isValid }
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      user: '',
      role: UserRoleType.SystemAdmin
    },
    resolver: yupResolver(validationSchema)
  });

  const userAccountOptions = useMemo(
    () => [
      {
        label: t('modal.assignUserRole.radio.option.admin'),
        value: UserRoleType.SystemAdmin
      },
      {
        label: t('modal.assignUserRole.radio.option.read'),
        value: UserRoleType.ReadAll
      },
      {
        label: t('modal.assignUserRole.radio.option.none'),
        value: UserRoleType.SystemUser
      }
    ],
    [t]
  );

  const onSubmit = async (formData: FormData) => {
    await dispatch(updateInternalUserRole(Number(formData.user), formData.role));
    await dispatch(getInternalUsers(sortFilter));
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        message: t('modal.assignUserRole.form.message.success')
      })
    );
    getUsers();
    closeModal();
  };

  const title = <h3 className="modal-title">{t('modal.assignUserRole.title')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      <Button disabled={!isValid} form="assign-user-role" type="submit" buttonTypes={['blue']}>
        {t('common.save')}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="small" height={313}>
      <form id="assign-user-role" className="assign-user-role-modal" onSubmit={handleSubmit(onSubmit)}>
        <div className="assign-user-role-modal__content">
          <Controller
            name="user"
            control={control}
            render={({ field: { onChange, name } }) => (
              <Select
                id="user"
                label={t('common.internalUser')}
                name={name}
                options={userOptions}
                onChange={(option) => {
                  if (Array.isArray(option)) {
                    [option] = option; // eslint-disable-line no-param-reassign
                  }
                  onChange(option.value);
                }}
                addPlaceholderOption
                required
              />
            )}
          />
          <Controller
            name="role"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <Radio id="role" name={name} label={t('common.role')} options={userAccountOptions} defaultValue={value} onChange={onChange} />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default AssignUserRoleModal;
